import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Button,
    Divider
} from '@mui/material';
import { Dashboard, ArrowBack } from '@mui/icons-material';
import api from '../../services/api';

const karatMap = {
    '22K': '22K',
    '22/22K': '22K',
    '24K': '24K',
    '24/24K': '24K',
    '18K': '18K',
    'PT95': 'PT95',
    'WG': 'WG'
};
const karatOrder = ['22K', '24K', '18K', 'PT95', 'WG'];

const ShopKarigarOverview = () => {
    const [karigars, setKarigars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchKarigars = async () => {
            try {
                const res = await api.get('/api/shop/karigar-overview-summary');
                setKarigars(res.data);
            } catch (err) {
                setError('Failed to fetch karigars');
            }
            setLoading(false);
        };
        fetchKarigars();
    }, []);

    const getPerKaratTotals = (obj = {}) => {
        const totals = {};
        Object.entries(obj).forEach(([k, v]) => {
            const mapped = karatMap[k] || k;
            totals[mapped] = (totals[mapped] || 0) + v;
        });
        return totals;
    };

    return (
        <Box sx={{
            width: '100vw',
            minHeight: '100vh',
            background: '#f7f7f7',
            px: 0,
            py: 0,
            overflowX: 'hidden',
            position: 'relative',
        }}>
            {/* Header Row */}
            <Box sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: '#fff',
                borderBottom: '2px solid #e0e0e0',
                px: 3,
                py: 2,
                position: 'sticky',
                top: 0,
                zIndex: 10,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Dashboard sx={{ fontSize: 40, color: 'primary.main' }} />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Karigar Overview</Typography>
                </Box>
                <Button startIcon={<ArrowBack />} onClick={() => window.history.back()} variant="contained" color="primary" sx={{ fontWeight: 600 }}>Back</Button>
            </Box>

            {/* Summary Row */}
            <Box sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
                px: 3,
                py: 2,
                background: '#fff',
                borderBottom: '1px solid #e0e0e0',
            }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                    Gold Given & Receipt Overview (per Karat)
                </Typography>
            </Box>

            {/* Table Row */}
            <Box sx={{
                width: '100%',
                px: 3,
                py: 2,
                overflowY: 'auto',
            }}>
                <Paper elevation={2} sx={{ width: '100%', p: 0, mb: 2 }}>
                    <Box sx={{ px: 2, py: 2, borderBottom: '1px solid #e0e0e0', background: '#fafafa' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>Karigar Metal Summary</Typography>
                    </Box>
                    <Divider />
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Alert severity="error">{error}</Alert>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ background: '#f5f5f5' }}>
                                        <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>Karigar</TableCell>
                                        {karatOrder.map(karat => (
                                            <TableCell key={karat} align="center" sx={{ fontWeight: 700, minWidth: 80 }}>{karat} Given</TableCell>
                                        ))}
                                        {karatOrder.map(karat => (
                                            <TableCell key={karat + '-rec'} align="center" sx={{ fontWeight: 700, minWidth: 80 }}>{karat} Received</TableCell>
                                        ))}
                                        {karatOrder.map(karat => (
                                            <TableCell key={karat + '-bal'} align="center" sx={{ fontWeight: 700, minWidth: 80 }}>{karat} Balance</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {karigars.map(k => {
                                        const given = getPerKaratTotals(k.totalGiven);
                                        const received = getPerKaratTotals(k.totalReceived);
                                        return (
                                            <TableRow key={k.abbreviation}>
                                                <TableCell sx={{ fontWeight: 500 }}>{k.abbreviation} - {k.name}</TableCell>
                                                {karatOrder.map(karat => {
                                                    const val = parseFloat(given[karat]) || 0;
                                                    return (
                                                        <TableCell key={karat} align="center">{val.toFixed(2)}</TableCell>
                                                    );
                                                })}
                                                {karatOrder.map(karat => {
                                                    const val = parseFloat(received[karat]) || 0;
                                                    return (
                                                        <TableCell key={karat + '-rec'} align="center">{val.toFixed(2)}</TableCell>
                                                    );
                                                })}
                                                {karatOrder.map(karat => {
                                                    const givenVal = parseFloat(given[karat]) || 0;
                                                    const receivedVal = parseFloat(received[karat]) || 0;
                                                    const bal = givenVal - receivedVal;
                                                    
                                                    let color = 'inherit';
                                                    let displayValue = bal.toFixed(2);
                                                    
                                                    // If balance is negative (received more than given), show in red
                                                    if (bal < 0) {
                                                        color = '#d32f2f'; // Red
                                                        displayValue = bal.toFixed(2); // Shows negative number
                                                    } 
                                                    // If balance is positive (still owed), show in green
                                                    else if (bal > 0) {
                                                        color = '#388e3c'; // Green
                                                        displayValue = bal.toFixed(2);
                                                    }
                                                    // If balance is zero, show in regular color
                                                    else {
                                                        color = 'inherit';
                                                        displayValue = '0.00';
                                                    }
                                                    
                                                    return (
                                                        <TableCell key={karat + '-bal'} align="center" sx={{ color, fontWeight: 600 }}>
                                                            {displayValue}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            </Box>
        </Box>
    );
};

export default ShopKarigarOverview;
